import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Brain, Target, TrendingUp, AlertTriangle, CheckCircle, Info, Clock } from 'lucide-react';
import { useLanguage } from '@/LanguageContext';

const ExplainableAI = ({ analysis }) => {
  const { t } = useLanguage();
  const [expandedDomain, setExpandedDomain] = useState(null);
  
  // Check if we have structured analysis
  const structured = analysis?.structured;
  const isExplainable = analysis?.is_explainable && structured;
  
  // Fallback to raw analysis if not structured
  if (!isExplainable) {
    return (
      <div className="p-4 bg-[#F3F4F6] rounded-xl">
        <div className="flex items-center space-x-2 mb-2">
          <Brain className="text-[#0F4C81]" size={20} />
          <span className="font-semibold text-[#111827]">{t('ai.analysis')}</span>
        </div>
        <p className="text-sm text-[#4B5563] whitespace-pre-wrap">
          {analysis?.analysis || t('ai.noAnalysisAvailable')}
        </p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_track': return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', icon: CheckCircle };
      case 'needs_attention': return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', icon: Info };
      case 'concerning': return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: AlertTriangle };
      default: return { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300', icon: Info };
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'low': return 'bg-green-500';
      case 'moderate': return 'bg-yellow-500';
      case 'high': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getConfidenceBar = (confidence) => {
    const percent = Math.round((confidence || 0) * 100);
    return (
      <div className="flex items-center space-x-2">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-[#0F4C81] rounded-full transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
        <span className="text-xs font-semibold text-[#4B5563] min-w-[40px]">{percent}%</span>
      </div>
    );
  };

  const domainLabels = {
    motor_development: { label: t('ai.motorDevelopment'), icon: '🏃' },
    cognitive_development: { label: t('ai.cognitiveDevelopment'), icon: '🧠' },
    social_emotional: { label: t('ai.socialEmotional'), icon: '💝' },
    language_communication: { label: t('ai.languageCommunication'), icon: '💬' }
  };

  const toggleDomain = (domain) => {
    setExpandedDomain(expandedDomain === domain ? null : domain);
  };

  return (
    <div className="space-y-4" data-testid="explainable-ai">
      {/* Overall Summary Card */}
      <div className="bg-gradient-to-br from-[#0F4C81] to-[#1e5a9a] rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Brain size={28} />
            <h3 className="text-xl font-bold">{t('ai.aiInsights')}</h3>
          </div>
          <div className={`px-4 py-2 rounded-full font-bold text-sm ${getRiskLevelColor(structured.overall_risk_level)} text-white`}>
            {structured.overall_risk_level?.toUpperCase()} {t('common.risk')}
          </div>
        </div>
        
        {/* Simple Explanation for low-literacy users */}
        {structured.simple_explanation && (
          <div className="bg-white/10 rounded-xl p-4 mb-4">
            <p className="text-lg">{structured.simple_explanation}</p>
          </div>
        )}
        
        {/* Full Summary */}
        <p className="text-white/90 text-sm">{structured.summary}</p>
        
        {/* Confidence Score */}
        <div className="mt-4 pt-4 border-t border-white/20">
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/70">{t('ai.overallConfidence')}</span>
            <span className="font-bold">{Math.round((structured.overall_confidence || 0) * 100)}%</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full mt-2 overflow-hidden">
            <div 
              className="h-full bg-white rounded-full"
              style={{ width: `${(structured.overall_confidence || 0) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Domain Breakdown */}
      {structured.domains && (
        <div className="space-y-3">
          <h4 className="font-bold text-[#111827] flex items-center space-x-2">
            <Target size={18} className="text-[#0F4C81]" />
            <span>{t('ai.developmentalDomains')}</span>
          </h4>
          
          {Object.entries(structured.domains).map(([key, domain]) => {
            const statusStyle = getStatusColor(domain.status);
            const StatusIcon = statusStyle.icon;
            const domainInfo = domainLabels[key] || { label: key, icon: '📊' };
            const isExpanded = expandedDomain === key;
            
            return (
              <div 
                key={key} 
                className={`rounded-xl border-2 ${statusStyle.border} overflow-hidden transition-all`}
                data-testid={`domain-${key}`}
              >
                {/* Header - Always visible */}
                <div 
                  className={`p-4 cursor-pointer ${statusStyle.bg} hover:opacity-90 transition-opacity`}
                  onClick={() => toggleDomain(key)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{domainInfo.icon}</span>
                      <div>
                        <h5 className={`font-bold ${statusStyle.text}`}>{domainInfo.label}</h5>
                        <span className={`text-xs font-semibold uppercase ${statusStyle.text}`}>
                          {domain.status?.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <StatusIcon className={statusStyle.text} size={20} />
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>
                </div>
                
                {/* Expanded Content */}
                {isExpanded && (
                  <div className="p-4 bg-white border-t border-slate-200 space-y-4">
                    {/* Confidence */}
                    <div>
                      <span className="text-xs font-semibold text-[#4B5563] uppercase">{t('ai.confidence')}</span>
                      {getConfidenceBar(domain.confidence)}
                    </div>
                    
                    {/* Findings */}
                    {domain.findings && (
                      <div>
                        <span className="text-xs font-semibold text-[#4B5563] uppercase">{t('ai.findings')}</span>
                        <p className="text-sm text-[#111827] mt-1">{domain.findings}</p>
                      </div>
                    )}
                    
                    {/* Why This Matters */}
                    {domain.why_this_matters && (
                      <div className="bg-blue-50 rounded-lg p-3">
                        <span className="text-xs font-semibold text-[#0F4C81] uppercase flex items-center space-x-1">
                          <Info size={14} />
                          <span>{t('ai.whyThisMatters')}</span>
                        </span>
                        <p className="text-sm text-[#0F4C81] mt-1">{domain.why_this_matters}</p>
                      </div>
                    )}
                    
                    {/* Recommendation */}
                    {domain.recommendation && (
                      <div className="bg-green-50 rounded-lg p-3">
                        <span className="text-xs font-semibold text-green-800 uppercase flex items-center space-x-1">
                          <TrendingUp size={14} />
                          <span>{t('ai.recommendation')}</span>
                        </span>
                        <p className="text-sm text-green-800 mt-1">{domain.recommendation}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Red Flags */}
      {structured.red_flags?.length > 0 && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
          <h4 className="font-bold text-red-800 flex items-center space-x-2 mb-3">
            <AlertTriangle size={18} />
            <span>{t('ai.redFlags')}</span>
          </h4>
          <ul className="space-y-2">
            {structured.red_flags.map((flag, idx) => (
              <li key={idx} className="text-sm text-red-700 flex items-start space-x-2">
                <span className="text-red-500 mt-0.5">•</span>
                <span>{flag}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Strengths */}
      {structured.strengths?.length > 0 && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
          <h4 className="font-bold text-green-800 flex items-center space-x-2 mb-3">
            <CheckCircle size={18} />
            <span>{t('ai.strengths')}</span>
          </h4>
          <ul className="space-y-2">
            {structured.strengths.map((strength, idx) => (
              <li key={idx} className="text-sm text-green-700 flex items-start space-x-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>{strength}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Next Steps & Follow-up */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {structured.next_steps?.length > 0 && (
          <div className="bg-[#F3F4F6] rounded-xl p-4">
            <h4 className="font-bold text-[#111827] flex items-center space-x-2 mb-3">
              <Target size={18} className="text-[#0F4C81]" />
              <span>{t('ai.nextSteps')}</span>
            </h4>
            <ol className="space-y-2">
              {structured.next_steps.map((step, idx) => (
                <li key={idx} className="text-sm text-[#4B5563] flex items-start space-x-2">
                  <span className="font-bold text-[#0F4C81] min-w-[20px]">{idx + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
        
        {structured.follow_up_timeline && (
          <div className="bg-[#FEF3C7] rounded-xl p-4">
            <h4 className="font-bold text-[#92400E] flex items-center space-x-2 mb-3">
              <Clock size={18} />
              <span>{t('ai.followUpTimeline')}</span>
            </h4>
            <p className="text-lg font-bold text-[#92400E]">{structured.follow_up_timeline}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExplainableAI;
